import { useState, useEffect } from 'react';

// ==============================|| LOCAL STORAGE HOOKS ||============================== //

export default function useLocalStorage<ValueType>(key: string, defaultValue: ValueType): [ValueType, (newValue: ValueType) => void] {
  const [value, setValue] = useState(() => {
    try {
      const storedValue = typeof window !== 'undefined' ? localStorage.getItem(key) : null;
      return storedValue === null ? defaultValue : JSON.parse(storedValue);
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return defaultValue;
    }
  });

  useEffect(() => {
    const listener = (e: StorageEvent) => {
      if (e.storageArea === localStorage && e.key === key) {
        try {
          setValue(e.newValue ? JSON.parse(e.newValue) : e.newValue);
        } catch (error) {
          console.error(`Error parsing localStorage change for key "${key}":`, error);
        }
      }
    };
    window.addEventListener('storage', listener);

    return () => {
      window.removeEventListener('storage', listener);
    };
  }, [key, defaultValue]);

  const setValueInLocalStorage = (newValue: ValueType) => {
    setValue((currentValue: any) => {
      const result = typeof newValue === 'function' ? newValue(currentValue) : newValue;
      try {
        if (typeof window !== 'undefined' && key) {
          localStorage.setItem(key, JSON.stringify(result));
        }
      } catch (error) {
        // Handle QuotaExceededError and others gracefully to prevent app crash
        console.error(`Error writing localStorage key "${key}":`, error);
      }
      return result;
    });
  };

  return [value as ValueType, setValueInLocalStorage];
}
